import React, { lazy, useEffect, useCallback, useState, Suspense, memo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { withStyles, makeStyles } from "@material-ui/core/styles"
import Dialog from "@material-ui/core/Dialog"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import MuiDialogContent from "@material-ui/core/DialogContent"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import modalStatusHandler from "@/redux/actions/modalAction"
import { resetActivateProcess } from "@/redux/actions/activateProjectProcessAction"
import CornerImg from "@/assets/images/corner-img.png"
import Loading from "@/components/common/Loading"

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(5),
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
        width: "28px",
        height: "28px",
        "&:hover": {
            backgroundColor: theme.palette.primary.secondary,
        },
    },
})

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    )
})

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(0),
    },
}))(MuiDialogContent)

const useStyles = makeStyles(() => ({
    dialogContent: {
        zIndex: "1",
    },
    popupCornerImage: {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: "-1",
        maxWidth: "200px",
    },
}))

const importView = (component) =>
    lazy(() => import(`./project-create-modal/${component}`).catch(() => console.log("import projectCreateModal error")))

const initProjectSponsor = {
    sponsorFirstName: "",
    sponsorLastName: "",
    sponsorEmail: "",
    confirm: false,
    newUser: false,
}

const initEmailReminder = {
    reminderType: "Formal",
    reminderFrequency: "Weekly",
    id: 5,
}

const initContainerDetail = {
    step: 1,
    newProject: null,
    projectSponsor: initProjectSponsor,
    emailReminder: initEmailReminder,
    participantList: [],
}
const ProjectDialogContainer = memo(() => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const [modals] = useState(() => [
        {
            step: 1,
            component: "ActiveProject",
            id: "active-project-modal",
        },
        {
            step: 2,
            component: "ProjectDetail",
            id: "project-detail-modal",
        },
        {
            step: 3,
            component: "AddSponsor",
            id: "add-sponsor-modal",
        },
        {
            step: 4,
            component: "AddParticipant",
            id: "add-participants-modal",
        },
        {
            step: 5,
            component: "EmailNotification",
            id: "email-notification-modal",
        },
        {
            step: 6,
            component: "FinalConfirmation",
            id: "final-confirmation-modal",
        },
        {
            step: 7,
            component: "ProcessingReview",
            id: "processing-review",
        },
    ])
    const [views, setViews] = useState()
    // const [step, setStep] = useState(1)
    // const [newProject, setNewProject] = useState(null)
    // const [projectSponsor, setProjectSponsor] = useState(initProjectSponsor)
    // const [participantList, setParticipantList] = useState([])
    // const [emailReminder, setEmailReminder] = useState(initEmailReminder)
    const { modalShow } = useSelector((state) => state.modalReducer)

    const [containerDetail, setContainerDetail] = useState(initContainerDetail)
    const { step, newProject, projectSponsor, participantList, emailReminder } = containerDetail
    const prevStep = useCallback(() => {
        setContainerDetail((prev) => ({ ...prev, step: prev.step - 1 }))
    }, [step])

    const nextStep = useCallback(() => {
        setContainerDetail((prev) => ({ ...prev, step: prev.step + 1 }))
    }, [step])

    const handleSetNewProject = useCallback(
        (project) => {
            setContainerDetail((prev) => ({ ...prev, newProject: project }))
        },
        [newProject]
    )

    const handleSetProjectSponsor = useCallback(
        (sponsor) => {
            setContainerDetail((prev) => ({ ...prev, projectSponsor: sponsor }))
        },
        [projectSponsor]
    )

    const handleSetParticipantListMain = useCallback(
        (participants) => {
            setContainerDetail((prev) => ({ ...prev, participantList: participants }))
        },
        [participantList]
    )

    const handleEmailReminder = useCallback(
        (emailReminderSetting) => {
            setContainerDetail((prev) => ({ ...prev, emailReminder: emailReminderSetting }))
        },
        [emailReminder]
    )

    const handleClose = () => {
        // reset everything
        dispatch(
            modalStatusHandler({
                modalProjectId: null,
                currentStep: 1,
                modalShow: false,
            })
        )
        setContainerDetail(initContainerDetail)
        dispatch(resetActivateProcess)
    }

    const loadViews = async () => {
        const modalToShow = modals.find((modal) => {
            if (step === modal.step) {
                return modal
            }
            return null
        })
        const componentPromises = async () => {
            const View = await importView(modalToShow.component)
            return (
                <View
                    nextStep={nextStep}
                    handleClose={handleClose}
                    prevStep={prevStep}
                    handleSetNewProject={handleSetNewProject}
                    handleSetProjectSponsor={handleSetProjectSponsor}
                    handleSetParticipantListMain={handleSetParticipantListMain}
                    handleEmailReminder={handleEmailReminder}
                    initProjectSponsor={initProjectSponsor}
                    initEmailReminder={initEmailReminder}
                    containerDetail={containerDetail}
                />
            )
        }
        const view = await componentPromises()
        setViews(view)
    }

    useEffect(() => {
        loadViews()
    }, [step])

    return (
        <>
            <Suspense fallback={<Loading />}>
                <Dialog aria-labelledby="customized-dialog-title" open={modalShow} maxWidth="md" fullWidth>
                    <DialogTitle className={classes.closeButton} id="customized-dialog-title" onClose={handleClose} />
                    <DialogContent className={classes.dialogContent}>
                        <img src={CornerImg} alt="popup-corner" className={classes.popupCornerImage} />
                        {views}
                    </DialogContent>
                </Dialog>
            </Suspense>
        </>
    )
})

export default ProjectDialogContainer
